/**
 * MatchStatusBadge Component
 * Displays a colored badge for transaction match status values
 */

import type { VariantProps } from 'class-variance-authority';
import { Badge } from '@/components/ui/badge';
import type { badgeVariants } from '@/components/ui/badge-variants';

type MatchStatus = 'unmatched' | 'suggested' | 'confirmed' | 'auto_matched' | 'dismissed' | 'platform_payment';

interface MatchStatusBadgeProps {
  status: MatchStatus | string;
  id?: string;
}

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

const STATUS_CONFIG: Record<string, { label: string; variant: BadgeVariant }> = {
  unmatched: { label: 'Unmatched', variant: 'secondary' },
  suggested: { label: 'Suggested', variant: 'warning' },
  confirmed: { label: 'Confirmed', variant: 'success' },
  auto_matched: { label: 'Auto Matched', variant: 'default' },
  dismissed: { label: 'Dismissed', variant: 'danger' },
  platform_payment: { label: 'Platform', variant: 'info' },
};

export function MatchStatusBadge({ status, id }: MatchStatusBadgeProps): JSX.Element {
  const config = STATUS_CONFIG[status] ?? { label: status, variant: 'secondary' as const };

  return (
    <Badge
      variant={config.variant}
      data-testid={id ? `match-status-badge-${id}` : undefined}
      className="rounded-full"
    >
      {config.label}
    </Badge>
  );
}
